import { useMemo, useState, type FormEvent } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { useSeo } from "@/hooks/useSeo";
import PageHeader from "@/components/feature/PageHeader";
import NewsCard from "@/components/feature/NewsCard";
import NewsModal from "@/components/feature/NewsModal";
import WorkCard from "@/components/feature/WorkCard";
import { newsList, type NewsItem } from "@/mocks/news";
import { worksList } from "@/mocks/works";

export default function SearchPage() {
  const [searchParams, setSearchParams] = useSearchParams();
  const keyword = (searchParams.get("q") || "").trim();
  const [input, setInput] = useState(keyword);
  const [activeNews, setActiveNews] = useState<NewsItem | null>(null);

  useSeo({
    title: keyword ? `搜索“${keyword}”结果 - 佐佐木明希官网` : "搜索 - 佐佐木明希官网",
    description: keyword
      ? `关于“${keyword}”的搜索结果，覆盖佐佐木明希的明星资讯与影视作品，帮助你快速找到相关内容。`
      : "在佐佐木明希官网中搜索明星资讯与影视作品。",
    keywords: "佐佐木明希搜索,站内搜索,明星资讯,影视作品",
  });

  const { newsResults, workResults } = useMemo(() => {
    const q = keyword.toLowerCase();
    if (!q) return { newsResults: [], workResults: [] };
    const news = newsList.filter((item) =>
      [item.title, item.summary, item.type, ...item.tags].join(" ").toLowerCase().includes(q)
    );
    const works = worksList.filter((item) =>
      [item.title, item.role, item.category, item.director, ...item.genres, ...item.cast]
        .join(" ")
        .toLowerCase()
        .includes(q)
    );
    return { newsResults: news, workResults: works };
  }, [keyword]);

  const total = newsResults.length + workResults.length;

  const submit = (event: FormEvent) => {
    event.preventDefault();
    const q = input.trim();
    if (!q) return;
    setSearchParams({ q });
  };

  return (
    <>
      <PageHeader
        crumbs={[{ label: "首页", path: "/" }, { label: "搜索结果" }]}
        title={keyword ? `搜索“${keyword}”` : "全站搜索"}
        lead="输入关键词，即可检索佐佐木明希相关的明星资讯与影视作品，包括标题、类型、角色与题材信息。"
      >
        <form onSubmit={submit} className="max-w-xl">
          <div className="flex flex-col gap-3 sm:flex-row">
            <div className="flex flex-1 items-center gap-2 rounded-md border border-background-300 bg-background-50 px-4 py-2.5 focus-within:border-primary-400">
              <i className="ri-search-line text-base text-foreground-500" aria-hidden="true" />
              <input
                type="text"
                name="q"
                value={input}
                onChange={(e) => setInput(e.target.value)}
                placeholder="输入关键词，例如：东京夜航、电影、访谈"
                aria-label="搜索关键词"
                className="w-full bg-transparent text-sm text-foreground-900 outline-none placeholder:text-foreground-400"
              />
            </div>
            <button
              type="submit"
              className="inline-flex items-center justify-center gap-2 whitespace-nowrap rounded-md bg-primary-500 px-6 py-2.5 text-sm font-label font-medium text-background-50 transition-colors hover:bg-primary-600"
            >
              重新检索
            </button>
          </div>
        </form>
      </PageHeader>

      <section className="bg-background-50 py-12 md:py-16">
        <div className="mx-auto w-full max-w-[1200px] px-4 md:px-6">
          {!keyword ? (
            <div className="rounded-lg border border-dashed border-background-300 bg-background-100 py-20 text-center">
              <i className="ri-search-2-line text-4xl text-foreground-400" aria-hidden="true" />
              <p className="mt-4 font-heading text-lg text-foreground-800">请输入关键词开始搜索</p>
              <p className="mt-2 text-sm text-foreground-500">支持搜索作品名称、角色、题材与资讯标题</p>
            </div>
          ) : total === 0 ? (
            <div className="rounded-lg border border-dashed border-background-300 bg-background-100 py-20 text-center">
              <i className="ri-emotion-sad-line text-4xl text-foreground-400" aria-hidden="true" />
              <p className="mt-4 font-heading text-lg text-foreground-800">
                没有找到与“{keyword}”相关的内容
              </p>
              <p className="mt-2 text-sm text-foreground-500">换个关键词，或从下面的推荐内容开始浏览</p>
              <div className="mt-7 flex flex-wrap justify-center gap-3">
                <Link
                  to="/works"
                  className="inline-flex items-center gap-2 whitespace-nowrap rounded-md bg-primary-500 px-5 py-2.5 text-sm font-label font-medium text-background-50 transition-colors hover:bg-primary-600"
                >
                  <i className="ri-film-line" aria-hidden="true" />
                  浏览影视作品
                </Link>
                <Link
                  to="/news"
                  className="inline-flex items-center gap-2 whitespace-nowrap rounded-md border border-background-300 px-5 py-2.5 text-sm font-label font-medium text-foreground-800 transition-colors hover:border-primary-400 hover:text-primary-700"
                >
                  <i className="ri-newspaper-line" aria-hidden="true" />
                  浏览明星资讯
                </Link>
              </div>
            </div>
          ) : (
            <>
              <div className="rounded-lg border border-background-200 bg-background-100 p-5">
                <p className="text-sm text-foreground-700">
                  关于 <span className="font-semibold text-foreground-950">“{keyword}”</span> 的结果统计：
                  <span className="ml-2 text-primary-700">资讯 {newsResults.length} 条</span>
                  <span className="mx-1 text-foreground-400">·</span>
                  <span className="text-accent-700">作品 {workResults.length} 部</span>
                </p>
              </div>

              {newsResults.length > 0 && (
                <div className="mt-10">
                  <div className="mb-5 flex items-center gap-3">
                    <h2 className="font-heading text-xl font-bold text-foreground-950">资讯搜索结果</h2>
                    <span className="rounded-full bg-primary-100 px-2.5 py-0.5 text-xs text-primary-800">
                      {newsResults.length}
                    </span>
                  </div>
                  <div className="grid grid-cols-1 gap-4 lg:grid-cols-2">
                    {newsResults.map((item) => (
                      <NewsCard key={item.id} item={item} onOpen={setActiveNews} layout="row" />
                    ))}
                  </div>
                </div>
              )}

              {workResults.length > 0 && (
                <div className="mt-12">
                  <div className="mb-5 flex items-center gap-3">
                    <h2 className="font-heading text-xl font-bold text-foreground-950">作品搜索结果</h2>
                    <span className="rounded-full bg-accent-100 px-2.5 py-0.5 text-xs text-accent-900">
                      {workResults.length}
                    </span>
                  </div>
                  <div className="grid grid-cols-1 gap-5 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4">
                    {workResults.map((item) => (
                      <WorkCard key={item.id} item={item} />
                    ))}
                  </div>
                </div>
              )}
            </>
          )}
        </div>
      </section>

      <NewsModal item={activeNews} onClose={() => setActiveNews(null)} />
    </>
  );
}